import React, { useEffect, useState, useRef, useCallback } from "react";
import { connect } from "react-redux";
import axios from "axios";
import data from "../../../../assets/apiRoutes.json";
import { GridLayout } from "../../../../common/GridLayout";
import {
    Column,
    DataGrid,
    GroupPanel,
    Grouping,
    FilterRow,
    FilterPanel,
    HeaderFilter,
    SearchPanel,
    Sorting,
    Paging,
    Selection,
    Toolbar,
    Item,
    Export,
    Pager,
    ColumnChooser,
    ColumnChooserSearch,
    StateStoring,
} from "devextreme-react/data-grid";

/**
 * Reusable WM transactions grid backed by wm/Transaction/GetAllTransactions.
 * Parameters:
 *   - customerId      : IDMASTER rowid of the customer (required)
 *   - transactionType : WMTRANSACTIONHEADER.TransactionType flag (e.g. "R"); "" = all types
 *
 * Only open (non-committed) transactions are returned: the endpoint excludes
 * committed (Status <> 'Z') by default when no Status is passed, so the "Open X"
 * tabs need no status filter here.
 */
const CustomerTransactions = (props) => {
    const { customerId, transactionType, gridKey } = props;
    const accessToken = props.oidc.user.access_token;
    const dataGrid = useRef(null);
    const [rows, setRows] = useState([]);
    const [currentLayout, setCurrentLayout] = useState("Default");

    const allowedPageSizes = [10, 20, 100, 500, "all"];
    const stateKey = gridKey;
    const loadState = useCallback(() => GridLayout.sendStorageRequest(stateKey, "GET", accessToken, null, currentLayout), [stateKey, currentLayout]);
    const saveState = useCallback((state) => GridLayout.sendStorageRequest(stateKey, "POST", accessToken, state, currentLayout), [stateKey, currentLayout]);

    useEffect(() => {
        if (!customerId) {
            setRows([]);
            return;
        }
        // No Status is sent — the endpoint excludes committed (Status <> 'Z')
        // server-side by default, which is exactly the "open transactions" set.
        const request = {
            RCustomer: customerId,
            Transactiontype: transactionType || "",
        };
        axios.post(`${window._env_.REACT_APP_APIS}/${data.Receipt.GetAllTransactions}`, request, {
            headers: {
                "Content-Type": "application/json",
                access_token: accessToken,
                Authorization: `Bearer ${accessToken}`,
            },
        }).then((res) => {
            setRows(Array.isArray(res.data) ? res.data : []);
        }).catch((err) => {
            console.error("Failed to load customer transactions:", err);
            setRows([]);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customerId, transactionType]);

    return (
        <DataGrid
            onExporting={(e) => {
                if (window.handleDevExtremeDataGridExport) {
                    window.handleDevExtremeDataGridExport(e);
                }
            }}
            ref={dataGrid}
            dataSource={rows}
            keyExpr="rowid"
            allowColumnReordering={true}
            allowColumnResizing={true}
            showBorders={true}
            hoverStateEnabled={true}
            columnAutoWidth={true}
            onSelectionChanged={(e) => {
                if (props.onTransactionSelected) {
                    const row = e.selectedRowsData && e.selectedRowsData.length === 1 ? e.selectedRowsData[0] : null;
                    props.onTransactionSelected(row ? row.rowid : null);
                }
            }}
        >
            <ColumnChooser enabled={true} title="Choose Column" width={290} height={290} sortOrder="asc">
                <ColumnChooserSearch enabled={true} editorOptions={{ placeholder: 'Search column' }} />
            </ColumnChooser>
            <Export enabled={true} allowExportSelectedData={true} />
            <Selection mode="single" />
            <Sorting visible={true} mode="multiple" />
            <Paging defaultPageSize={10} />
            <Pager visible={true} showPageSizeSelector={true} allowedPageSizes={allowedPageSizes} showNavigationButtons={true} />
            <FilterRow visible={true} />
            <FilterPanel visible={true} />
            <HeaderFilter visible={true} />
            <SearchPanel visible={true} width={240} placeholder="Search..." />
            <GroupPanel visible={true} />
            <Grouping autoExpandAll={false} />
            <StateStoring enabled={true} type="custom" customLoad={loadState} storageKey={stateKey} customSave={saveState} />

            {/* Default-visible columns (mirrors the AI8 layout) */}
            <Column dataField="recordNumber" caption="Record #" />
            <Column dataField="transactionnumber" caption="Transaction #" />
            <Column dataField="transactiondate" caption="Transaction" dataType="date" />
            <Column dataField="shipper" caption="Shipper" />
            <Column dataField="consignee" caption="Consignee" />
            <Column dataField="status" caption="Status" />
            <Column dataField="stage" caption="Stage" />
            <Column dataField="reference" caption="Reference" />
            <Column dataField="amount" caption="Amount" dataType="number" format="$ #,##0.00" />
            <Column dataField="userDefinedCode1" caption="Code 1" />
            <Column dataField="userDefinedCode2" caption="Code 2" />

            {/* Column-chooser (hidden by default) */}
            <Column dataField="actualreceivedshippeddatetime" caption="Actual Received Shipped Date" dataType="date" visible={false} />
            <Column dataField="checkByHours" caption="Check By Hours" dataType="date" visible={false} />
            <Column dataField="checkByPlanDate" caption="Check By Plan Date" dataType="date" visible={false} />
            <Column dataField="codAmount" caption="COD Amount" dataType="number" format="$ #,##0.00" visible={false} />
            <Column dataField="codAmountCollected" caption="COD Amount Collected" dataType="number" format="$ #,##0.00" visible={false} />
            <Column dataField="codCollectionDateTime" caption="COD Collection Date" dataType="date" visible={false} />
            <Column dataField="codPayMethod" caption="COD Pay Method" visible={false} />
            <Column dataField="userDefinedCode3" caption="Code 3" visible={false} />
            <Column dataField="userDefinedCode4" caption="Code 4" visible={false} />
            <Column dataField="userDefinedCode5" caption="Code 5" visible={false} />
            <Column dataField="userDefinedCode6" caption="Code 6" visible={false} />
            <Column dataField="userDefinedCode7" caption="Code 7" visible={false} />
            <Column dataField="userDefinedCode8" caption="Code 8" visible={false} />
            <Column dataField="userDefinedCode9" caption="Code 9" visible={false} />
            <Column dataField="userDefinedCode10" caption="Code 10" visible={false} />
            <Column dataField="commitDate" caption="Commit Date" dataType="date" visible={false} />
            <Column dataField="companyid" caption="Company ID" visible={false} />
            <Column dataField="consolidateInvoiceFlag" caption="Consolidate Invoice" visible={false} />
            <Column dataField="consolidatedInvoice" caption="Consolidated Invoice" visible={false} />
            <Column dataField="container" caption="Container #" visible={false} />
            <Column dataField="creationUserId" caption="Created By" visible={false} />
            <Column dataField="creationDateTime" caption="Creation Date" dataType="date" visible={false} />
            <Column dataField="userdefineddate1" caption="Date 1" dataType="date" visible={false} />
            <Column dataField="userdefineddate2" caption="Date 2" dataType="date" visible={false} />
            <Column dataField="userdefineddate3" caption="Date 3" dataType="date" visible={false} />
            <Column dataField="userdefineddate4" caption="Date 4" dataType="date" visible={false} />
            <Column dataField="userdefineddate5" caption="Date 5" dataType="date" visible={false} />
            <Column dataField="userdefineddate6" caption="Date 6" dataType="date" visible={false} />
            <Column dataField="userdefineddate7" caption="Date 7" dataType="date" visible={false} />
            <Column dataField="userdefineddate8" caption="Date 8" dataType="date" visible={false} />
            <Column dataField="userdefineddate9" caption="Date 9" dataType="date" visible={false} />
            <Column dataField="userdefineddate10" caption="Date 10" dataType="date" visible={false} />
            <Column dataField="deliveryPickupFlag" caption="Delivery Pickup" visible={false} />
            <Column dataField="deliveryPickupArrivedDateTime" caption="Delivery Pickup Arrive Date" dataType="date" visible={false} />
            <Column dataField="deliveryPickupDepartDateTime" caption="Delivery Pickup Depart Date" dataType="date" visible={false} />
            <Column dataField="deliveryPickupPlannedDateTime" caption="Delivery Pickup Planned Date" dataType="date" visible={false} />
            <Column dataField="dispatchOrderNumber" caption="Dispatch Order Number" visible={false} />
            <Column dataField="equipmentinterchangereport" caption="Equipment Interchange Report" visible={false} />
            <Column dataField="fiscalPeriod" caption="Fiscal Period" dataType="number" visible={false} />
            <Column dataField="fiscalYear" caption="Fiscal Year" dataType="number" visible={false} />
            <Column dataField="freightAmount" caption="Freight Amount" dataType="number" format="$ #,##0.00" visible={false} />
            <Column dataField="freightchargetype" caption="Freight Charge Type" visible={false} />
            <Column dataField="holdReason" caption="Hold Reason" visible={false} />
            <Column dataField="lastUpdateDateTime" caption="Last Updated" dataType="date" visible={false} />
            <Column dataField="lastUpdateUserId" caption="Last Updated By" visible={false} />
            <Column dataField="module" caption="Module" visible={false} />
            <Column dataField="pickStatus" caption="Pick Status" visible={false} />
            <Column dataField="planReference" caption="Plan Reference" visible={false} />
            <Column dataField="planResourcesCode" caption="Plan Resources Code" visible={false} />
            <Column dataField="planResourcesHours" caption="Plan Resources Hours" dataType="date" visible={false} />
            <Column dataField="plannedreceivedshippeddatetime" caption="Planned Received Shipped Date" dataType="date" visible={false} />
            <Column dataField="printBol" caption="Print Bol" visible={false} />
            <Column dataField="printLabel" caption="Print Label" visible={false} />
            <Column dataField="printReceipt" caption="Print Receipt" visible={false} />
            <Column dataField="putAwayByHours" caption="Put Away By Hours" dataType="date" visible={false} />
            <Column dataField="putAwayByPlanDate" caption="Put Away By Plan Date" dataType="date" visible={false} />
            <Column dataField="sendFax" caption="Send Fax" visible={false} />
            <Column dataField="stagingStartDate" caption="Staging Start Date" dataType="date" visible={false} />
            <Column dataField="taxAmount" caption="Tax Amount" dataType="number" format="$ #,##0.00" visible={false} />
            <Column dataField="taxBasis" caption="Tax Basis" visible={false} />
            <Column dataField="taxRate" caption="Tax Rate" dataType="number" visible={false} />
            <Column dataField="thirdPartyBillType" caption="Third Party Bill Type" visible={false} />
            <Column dataField="transferToDispatch" caption="Transfer To Dispatch" visible={false} />
            <Column dataField="transportationMode" caption="Transportation Mode" visible={false} />
            <Column dataField="transportationYardLeaveDate" caption="Transportation Yard Leave Date" dataType="date" visible={false} />
            <Column dataField="truckArrivalDateTime" caption="Truck Arrival Date" dataType="date" visible={false} />
            <Column dataField="truckDepartDateTime" caption="Truck Departure Date" dataType="date" visible={false} />
            <Column dataField="transactiontype" caption="Type" visible={false} />
            <Column dataField="unloadPickByHours" caption="Unload Pick By Hours" dataType="date" visible={false} />
            <Column dataField="unloadPickByPlanDate" caption="Unload Pick By Plan Date" dataType="date" visible={false} />
            <Column dataField="userdefinedquantity1" caption="Quantity 1" dataType="number" visible={false} />
            <Column dataField="userdefinedquantity2" caption="Quantity 2" dataType="number" visible={false} />
            <Column dataField="userdefinedquantity3" caption="Quantity 3" dataType="number" visible={false} />
            <Column dataField="userdefinedquantity4" caption="Quantity 4" dataType="number" visible={false} />
            <Column dataField="userdefinedquantity5" caption="Quantity 5" dataType="number" visible={false} />
            <Column dataField="userdefinedquantity6" caption="Quantity 6" dataType="number" visible={false} />
            <Column dataField="userdefinedquantity7" caption="Quantity 7" dataType="number" visible={false} />
            <Column dataField="userdefinedquantity8" caption="Quantity 8" dataType="number" visible={false} />
            <Column dataField="userdefinedquantity9" caption="Quantity 9" dataType="number" visible={false} />
            <Column dataField="userdefinedquantity10" caption="Quantity 10" dataType="number" visible={false} />
            <Column dataField="recordtype" caption="Record Type" visible={false} />
            <Column dataField="userdefinedreference1" caption="Reference 1" visible={false} />
            <Column dataField="userdefinedreference2" caption="Reference 2" visible={false} />
            <Column dataField="userdefinedreference3" caption="Reference 3" visible={false} />
            <Column dataField="userdefinedreference4" caption="Reference 4" visible={false} />
            <Column dataField="userdefinedreference5" caption="Reference 5" visible={false} />
            <Column dataField="userdefinedreference6" caption="Reference 6" visible={false} />
            <Column dataField="userdefinedreference7" caption="Reference 7" visible={false} />
            <Column dataField="userdefinedreference8" caption="Reference 8" visible={false} />
            <Column dataField="userdefinedreference9" caption="Reference 9" visible={false} />
            <Column dataField="userdefinedreference10" caption="Reference 10" visible={false} />
            <Column dataField="customer" caption="Customer" visible={false} />
            <Column dataField="customerName" caption="Customer Name" visible={false} />
            <Column dataField="pallets" caption="Pallets" dataType="number" visible={false} />
            <Toolbar>
                <Item name="exportButton" />
                <Item name="columnChooserButton" />
            </Toolbar>
        </DataGrid>
    );
};

function mapStateToProps(state) {
    return {
        oidc: state.oidc,
    };
}
export default connect(mapStateToProps)(CustomerTransactions);
